import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  Button,
  Form,
  Row,
  Col,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { FaSearch, FaPlus, FaEdit, FaTrash, FaFileExcel } from "react-icons/fa";
import DepartmentTopbar from "../SuperAdmin/components/DepartmentTopbar";
import DepartmentSidebar from "../SuperAdmin/components/DepertmentSidebar";
import MarketDataModal from "./MarketDataModal";

function MarketData() {
  const [marketData, setMarketData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem("token");

  const fetchMarketData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/list-market-data.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success && Array.isArray(response.data.data)) {
        setMarketData(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch market data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketData();
  }, []);

  const handleAdd = () => {
    setEditingData(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setEditingData(row);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        const response = await axios.post(
          `${API_URL}/delete-market-data.php`,
          { id },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.data.success) {
          toast.success("Data deleted successfully!");
          fetchMarketData();
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error("An error occurred during deletion.");
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingData(null);
  };

  const handleSave = () => {
    fetchMarketData();
    handleModalClose();
  };

  const filteredData = useMemo(() => {
    return marketData.filter((item) => {
      const matchesSearch = item.challan_no
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const itemDate = new Date(item.collection_date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && itemDate < start) return false;
      if (end && itemDate > end) return false;

      return matchesSearch;
    });
  }, [marketData, searchTerm, startDate, endDate]);

  const totalAmount = useMemo(
    () =>
      filteredData.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0),
    [filteredData]
  );

  const handleExport = () => {
    const dataToExport = filteredData.map((d) => ({
      "Challan No": d.challan_no,
      Amount: d.amount,
      "Total Coupons": d.total_of_coupon,
      "Collection Date": d.collection_date,
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "MarketData");
    XLSX.writeFile(workbook, "Market_Data_Report.xlsx");
  };

  const columns = [
    { name: "Challan No", selector: (row) => row.challan_no, sortable: true },
    {
      name: "Amount (₹)",
      selector: (row) => parseFloat(row.amount).toLocaleString("en-IN"),
      sortable: true,
    },
    {
      name: "Total Coupons",
      selector: (row) => row.total_of_coupon,
      sortable: true,
    },
    {
      name: "Collection Date",
      selector: (row) => row.collection_date,
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-warning"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            <FaEdit />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDelete(row.id)}
          >
            <FaTrash />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex">
        <DepartmentSidebar />
        <div className="flex-grow-1 p-3" style={{ background: "#f0f2f5" }}>
          <Card className="shadow-sm">
            <Card.Header
              as="h5"
              className="d-flex justify-content-between align-items-center"
            >
              Market Data Management
              <Button variant="primary" size="sm" onClick={handleAdd}>
                <FaPlus /> Add New
              </Button>
            </Card.Header>
            <Card.Body>
              <Form className="p-3 border-bottom">
                <Row className="g-3 align-items-end">
                  <Col md={3}>
                    <Form.Label>Search by Challan No</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="search"
                        placeholder="Enter challan no..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Col>
                  <Col md={3} className="text-md-end">
                    <Button onClick={handleExport} variant="success">
                      <FaFileExcel className="me-2" />
                      Export
                    </Button>
                  </Col>
                </Row>
              </Form>
              <Row className="p-3 text-center">
                <Col>
                  <Card bg="light">
                    <Card.Body>
                      <Card.Title>Total Collection</Card.Title>
                      <Card.Text className="fs-4 fw-bold">
                        ₹{totalAmount.toLocaleString("en-IN")}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                responsive
                progressPending={loading}
              />
            </Card.Body>
          </Card>
        </div>
      </div>
      {showModal && (
        <MarketDataModal
          show={showModal}
          handleClose={handleModalClose}
          onSave={handleSave}
          data={editingData}
        />
      )}
    </>
  );
}

export default MarketData;
